import { useState } from 'react';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import SEO from '../components/SEO';
import { ChevronDown, ArrowRight } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import './FAQPage.css';
import { useTranslation } from "react-i18next";

const faqs = [
  {
    category: "orphan.generalSetup",
    items: [
      {
        question: "orphan.whatIsReviio",
        answer: "orphan.reviioIsAnAiPoweredReview"
      },
      {
        question: "orphan.howLongDoesItTakeTo",
        answer: "orphan.youCanGetStartedInLess"
      },
      {
        question: "orphan.doINeedAnyTechnicalSkills",
        answer: "orphan.notAtAllReviioIsDesigned"
      }
    ]
  },
  {
    category: "orphan.featuresAi",
    items: [
      {
        question: "orphan.howDoesTheAiAutoReply",
        answer: "orphan.ourAiAnalyzesTheSentimentAnd",
        articleLink: "/blog/automated-review-response-software-for-small-business",
        articleTitle: "orphan.readOurGuideOnAutomatedReview"
      },
      {
        question: "orphan.canICustomizeTheAiS",
        answer: "orphan.yesYouCanConfigureTheAi"
      },
      {
        question: "orphan.whatKindOfInsightsDoesReviio",
        answer: "orphan.reviioSAnalyticsLayerAutomaticallyAnalyz",
        articleLink: "/blog/extract-actionable-items-from-ai-review-reports",
        articleTitle: "orphan.learnHowToExtractActionableItems"
      },
      {
        question: "orphan.whatIsReviewGatingAndHow",
        answer: "orphan.ourReviewGateHelpsYouIntercept",
        articleLink: "/blog/intercept-negative-feedback-with-review-gating",
        articleTitle: "orphan.readMoreAboutInterceptingNegativeFeedbac"
      }
    ]
  },
  {
    category: "orphan.integrationsPlatforms",
    items: [
      {
        question: "orphan.whichPlatformsDoesReviioIntegrateWith",
        answer: "orphan.currentlyReviioSeamlesslyIntegratesWithG",
        articleLink: "/blog/manage-facebook-and-google-reviews-together",
        articleTitle: "orphan.discoverWhyManagingFacebookAndGoogle"
      },
      {
        question: "orphan.canIManageMultipleBusinessLocations",
        answer: "orphan.absolutelyReviioIsBuiltToScale"
      }
    ]
  },
  {
    category: "orphan.accountBilling",
    items: [
      {
        question: "orphan.isThereALimitOnHow",
        answer: "orphan.ourFlexiblePlansAreDesignedTo"
      },
      {
        question: "orphan.canIInviteMyTeamMembers",
        answer: "orphan.yesYouCanInviteYourStaff"
      },
      {
        question: "orphan.canITryReviioBeforeI",
        answer: "orphan.absolutelyWeProvideAnInteractiveDemoEnvi"
      }
    ]
  }
];

export default function FAQPage() {
    const { t } = useTranslation();
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Build FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.flatMap(cat => cat.items).map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <div className="faq-page">
      <SEO 
        title={t('faq.frequentlyAskedQuestions')} 
        description="Find answers to common questions about Reviio's AI review management platform, features, integrations, and pricing."

        schema={faqSchema}
      />
      
      {/* HEADER */}
      <SiteHeader variant="solid" />

      {/* HERO SECTION */}
      <section className="faq-hero">
        <div className="faq-container">
          <div className="faq-section-label">{t('faq.helpCenter')}</div>
          <h1 className="faq-hero-title">{t('faq.frequentlyAskedQuestions')}</h1>
          <p className="faq-hero-subtitle">{' ' + t('faq.everythingYouNeedToKnowAbout')}</p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="faq-content">
        <div className="faq-container faq-content-container">
          <div className="faq-grid">
            {faqs.map((category, catIndex) => (
              <div key={category.category} className="faq-category-block">
                <h2 className="faq-category-title">{t(category.category)}</h2>
                <div className="faq-accordion">
                  {category.items.map((item, itemIndex) => {
                    const isOpen = openItems[`${catIndex}-${itemIndex}`] || false;
                    return (
                      <div key={itemIndex} className={`faq-accordion-item ${isOpen ? 'open' : ''}`}>
                        <button 
                          className="faq-accordion-trigger" 
                          onClick={() => toggleItem(catIndex, itemIndex)}
                          aria-expanded={isOpen}
                        >
                          <span className="faq-question">{t(item.question)}</span>
                          <ChevronDown className={`faq-icon ${isOpen ? 'rotate' : ''}`} />
                        </button>
                        <div className="faq-accordion-content">
                          <p className="faq-answer">{t(item.answer)}</p>
                          {item.articleLink && (
                            <div className="faq-article-link">
                              <Link to={item.articleLink} className="faq-article-link-anchor">{t(item.articleTitle)}<ArrowRight size={14} /></Link>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="faq-cta-box">
            <h3>{t('faq.stillHaveQuestions')}</h3>
            <p>{t('faq.weReHereToHelpReach')}</p>
            <Link to="/#contact" className="faq-btn-secondary">{t('faq.contactSupport')}</Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );
}
